import axiosInstance from './axiosConfig';

export interface Notification {
  _id: string;
  recipientId: string;
  type: 'reminder' | 'mention' | 'activity_update' | 'lead_change' | 'system';
  title: string;
  message: string;
  status: 'unread' | 'read' | 'archived';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  leadId?: string;
  soId?: string;
  activityId?: string;
  reminderId?: string;
  senderId?: string;
  senderName?: string;
  emailSent: boolean;
  emailSentAt?: string;
  smsSent: boolean;
  smsSentAt?: string;
  pushSent: boolean;
  pushSentAt?: string;
  metadata?: any;
  notificationPreferences?: {
    email: boolean;
    sms: boolean;
    push: boolean;
  };
  createdAt: string;
  updatedAt: string;
  readAt?: string;
}

export interface NotificationFilters {
  type?: string;
  status?: 'unread' | 'read' | 'archived';
  leadId?: string;
  soId?: string;
  limit?: number;
  skip?: number;
}

export interface NotificationResponse {
  notifications: Notification[];
  total: number;
  unreadCount: number;
}

export interface UserSuggestion {
  id: string;
  name: string;
  username: string;
  email: string;
}

class NotificationService {
  private readonly API_URL = '/notifications';

  /**
   * Get user notifications
   */
  async getNotifications(filters: NotificationFilters = {}): Promise<NotificationResponse> {
    try {
      const params = new URLSearchParams();
      
      if (filters.type) params.append('type', filters.type);
      if (filters.status) params.append('status', filters.status);
      if (filters.leadId) params.append('leadId', filters.leadId);
      if (filters.soId) params.append('soId', filters.soId);
      if (filters.limit) params.append('limit', filters.limit.toString());
      if (filters.skip) params.append('skip', filters.skip.toString());

      const response = await axiosInstance.get(`${this.API_URL}?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Error getting notifications:', error);
      throw error;
    }
  }

  /**
   * Get unread notification count
   */
  async getUnreadCount(): Promise<number> {
    try {
      const response = await axiosInstance.get(`${this.API_URL}/unread-count`);
      return response.data.unreadCount;
    } catch (error) {
      console.error('Error getting unread count:', error);
      return 0;
    }
  }

  /**
   * Mark notification as read
   */
  async markAsRead(notificationId: string): Promise<void> {
    try {
      await axiosInstance.put(`${this.API_URL}/${notificationId}/read`);
    } catch (error) {
      console.error('Error marking notification as read:', error);
      throw error;
    }
  }

  /**
   * Mark all notifications as read
   */
  async markAllAsRead(): Promise<void> {
    try {
      await axiosInstance.put(`${this.API_URL}/mark-all-read`);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      throw error;
    }
  }

  /**
   * Delete notification
   */
  async deleteNotification(notificationId: string): Promise<void> {
    try {
      await axiosInstance.delete(`${this.API_URL}/${notificationId}`);
    } catch (error) {
      console.error('Error deleting notification:', error);
      throw error;
    }
  }

  /**
   * Search users for mentions
   */
  async searchUsersForMentions(query: string): Promise<UserSuggestion[]> {
    try {
      const response = await axiosInstance.get(`${this.API_URL}/users/search?query=${encodeURIComponent(query)}`);
      return response.data;
    } catch (error) {
      console.error('Error searching users for mentions:', error);
      return [];
    }
  }

  /**
   * Test notification (for development)
   */
  async testNotification(data: {
    recipientId: string;
    type: 'reminder' | 'mention' | 'activity_update' | 'lead_change' | 'system';
    title: string;
    message: string;
  }): Promise<Notification> {
    try {
      const response = await axiosInstance.post(`${this.API_URL}/test`, data);
      return response.data;
    } catch (error) {
      console.error('Error creating test notification:', error);
      throw error;
    }
  }

  /**
   * Format notification message for display
   */
  formatNotificationMessage(notification: Notification): string {
    switch (notification.type) {
      case 'mention':
        return `You were tagged by ${notification.senderName}`;
      case 'lead_change':
        return notification.message;
      case 'reminder':
        return `Reminder: ${notification.title}`;
      case 'activity_update':
        return notification.message;
      default:
        return notification.message;
    }
  }

  /**
   * Get notification icon based on type
   */
  getNotificationIcon(notification: Notification): string {
    switch (notification.type) {
      case 'mention':
        return '👤';
      case 'lead_change':
        return '📝';
      case 'reminder':
        return '⏰';
      case 'activity_update':
        return '📢';
      case 'system':
        return '⚙️';
      default:
        return '📌';
    }
  }

  /**
   * Get notification priority color
   */
  getNotificationPriorityColor(notification: Notification): string {
    switch (notification.priority) {
      case 'urgent':
        return '#f44336';
      case 'high':
        return '#ff9800';
      case 'medium':
        return '#2196f3';
      case 'low':
        return '#4caf50';
      default:
        return '#757575';
    }
  }

  /**
   * Check if notification is actionable
   */
  isActionable(notification: Notification): boolean {
    return Boolean(notification.leadId || notification.soId);
  }

  /**
   * Get notification action URL
   */
  getNotificationActionUrl(notification: Notification): string | null {
    if (notification.leadId) {
      return `/leads/${notification.leadId}`;
    }
    if (notification.soId) {
      return `/service-orders/${notification.soId}`;
    }
    return null;
  }
}

export const notificationService = new NotificationService();
